/**
 * 紧凑视图 - 基础列定义
 * 包含序号、注册号、日期、星期、航班号、航段等基础信息列
 */

import React from "react";
import { Tooltip } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getTimezoneByMode, TIMEZONE_OPTIONS } from "../../../../../../contexts/TimeZoneContext";
import FlightDateDisplay from "../../../../../../components/DateTimeDisplay/FlightDateDisplay";
import AircraftTag from "../../../../../../components/AircraftTag";
import RotationVisualizer from "../../RotationVisualizer";
import { FLIGHT_STATUS_COLORS, FLIGHT_STATUS, FLIGHT_STATUS_LABELS } from "../../../constants/flightConstants";

dayjs.extend(utc);
dayjs.extend(timezone);

// Hex转RGBA辅助函数
const hexToRgba = (hex, alpha) => {
    if (!hex) return "transparent";
    let c;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split("");
        if (c.length === 3) {
            c = [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c = "0x" + c.join("");
        return "rgba(" + [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(",") + "," + alpha + ")";
    }
    return hex;
};

/**
 * 获取基础信息列定义
 * @param {Object} options - 配置选项
 * @param {Function} options.getIndexColumn - 获取序号列的函数
 * @param {string} options.timeZoneMode - 时区模式
 * @param {Object} options.airportsMap - 机场映射
 * @param {Function} options.hasWarnings - 判断航班是否有警告
 * @param {Function} options.handleShowDetails - 显示详情回调
 * @param {Object} options.user - 当前用户
 * @param {Array} options.tableDataSource - 表格数据源（用于RotationVisualizer）
 * @returns {Array} 列定义数组
 */
export const getBasicColumns = ({ getIndexColumn, timeZoneMode, airportsMap, hasWarnings, handleShowDetails, user, tableDataSource }) => {
    return [
        getIndexColumn("序", 24, { fixed: "left" }),
        {
            title: "注册号",
            dataIndex: "aircraft_reg",
            key: "aircraft_reg",
            align: "center",
            className: "compact-col-center",
            width: 40,
            fixed: "left",
            render: (text) => <AircraftTag registration={text} />,
        },
        {
            title: "日期",
            dataIndex: "flight_date",
            key: "flight_date",
            align: "center",
            className: "compact-col-center monospaced-font",
            fixed: "left",
            width: 42,
            render: (flight_date, record) => {
                const { etd, std, departure_airport } = record;
                const primaryTime = etd || std;
                return <FlightDateDisplay etdOrStd={primaryTime} departureAirportCode={departure_airport} originalFlightDateForFallback={flight_date} />;
            },
        },
        {
            title: <Tooltip title="星期">D</Tooltip>,
            key: "day_of_week",
            align: "center",
            className: "compact-col-center",
            width: 14,
            render: (_, record) => {
                const { etd, std, departure_airport, flight_date } = record;
                const timeToConvert = etd || std || flight_date;
                if (!timeToConvert) return "-";
                try {
                    const targetTimezone = getTimezoneByMode(timeZoneMode, departure_airport, airportsMap);
                    const dayOfWeek = dayjs.utc(timeToConvert).tz(targetTimezone).day();
                    return dayOfWeek === 0 ? 7 : dayOfWeek;
                } catch (error) {
                    return "?";
                }
            },
        },
        {
            title: "航班",
            dataIndex: "flight_number",
            key: "flight_number",
            align: "center",
            className: "compact-col-center monospaced-font",
            fixed: "left",
            width: 38,
            onCell: (record) => ({
                style: {
                    backgroundColor:
                        record.status === FLIGHT_STATUS.SCHEDULED
                            ? "transparent"
                            : hexToRgba(FLIGHT_STATUS_COLORS[record.status] || FLIGHT_STATUS_COLORS.default, 0.15),
                },
            }),
            render: (text, record) => {
                let flightNumberStyle = {};
                if (timeZoneMode === TIMEZONE_OPTIONS.LOCAL) {
                    flightNumberStyle.color = "#722ed1";
                }
                if (hasWarnings(record)) {
                    flightNumberStyle.color = "#faad14";
                }
                const canViewDetails = user && (user.is_superuser || user.permissions?.includes("flight_operation:read"));
                const statusText = FLIGHT_STATUS_LABELS[record.status] || record.status;
                const warningText = hasWarnings(record) ? "此航班存在待处理事项或延误" : null;
                const tooltipTitle = (
                    <div>
                        <div>状态: {statusText}</div>
                        {record.booking_no && <div style={{ marginTop: 4 }}>订单: {record.booking_no}</div>}
                        {warningText && <div style={{ marginTop: 4, color: "#ff4d4f" }}>{warningText}</div>}
                    </div>
                );

                return (
                    <Tooltip title={tooltipTitle}>
                        {canViewDetails ? (
                            <a onClick={() => handleShowDetails(record)} style={flightNumberStyle}>
                                {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 1, fontSize: "9px", color: flightNumberStyle.color || "#faad14" }} />}
                                {text}
                            </a>
                        ) : (
                            <span style={flightNumberStyle}>
                                {hasWarnings(record) && <ExclamationCircleOutlined style={{ marginRight: 1, fontSize: "9px", color: flightNumberStyle.color || "#faad14" }} />}
                                {text}
                            </span>
                        )}
                    </Tooltip>
                );
            },
        },
        {
            title: <Tooltip title="轮换">R</Tooltip>,
            key: "rotation",
            align: "center",
            fixed: "left",
            width: 14,
            className: "compact-rotation-col",
            render: (_, record) => <RotationVisualizer flight={record} allFlights={tableDataSource} />,
        },
        // booking_no 列已移至 Rotation 列的 Tooltip 中展示
        {
            title: "航段",
            key: "route",
            align: "center",
            className: "compact-col-center monospaced-font",
            fixed: "left",
            width: 56,
            render: (_, record) => {
                // 特殊机场高亮配置
                // 销售地: HKG, HGH
                // 仅高亮起飞机场
                const SALES_AIRPORTS = [ "HKG", "HGH", "CGO"]; 

                const isSalesDeparture = SALES_AIRPORTS.includes(record.departure_airport);

                return (
                    <span>
                        <span style={isSalesDeparture ? { color: "#fa8c16" } : {}}>{record.departure_airport}</span>
                        <span style={{ color: "#bfbfbf", margin: "0 1px" }}>-</span>
                        <span>{record.arrival_airport}</span>
                    </span>
                );
            },
        },
    ];
};

// 客户列定义在 Monitor/components/customerColumn.jsx（列表视图与紧凑视图共用）
export { getCustomerColumn } from "../../customerColumn";

export default getBasicColumns;
